// 메일 본문 in-place 링크화 — 현재 jQuery + linkify.js 의 1:1 동작 보존.
//
// 보존 포인트 (docs/iframe-viewer/linkify-modernization-task.md §4.1.1 매핑):
//   C1  bare 도메인 자동 링크 차단 + sip 강제 (URL_ALLOW_RE / SIP_VALIDATE_RE)
//   C2  본문 기존 tel anchor 에도 listener 부착 + 중복 가드 (TEL_BRIDGE_BOUND)
//   C3  native bridge 반환값 비교 의미 (`result === false` 만 fallback)
//   C4  registerCustomProtocol('sip', true) — `sip:user@host` 형식 인식
//   H1  defaultProtocol: 'http' — http-only intranet 호환 (§5.5 (A))
//   H3  phone fallback 도 IGNORE_TAGS skip — 중첩 a 방지
//   L3  module top-level 등록 — 외부 import 가 linkify.find 를 먼저 호출해도 sip 인식 보장
//   TR  target='_blank' + rel='noopener noreferrer' — http(s)/ftp/www 외부 URL 한정 (§5.7 (B))
//   WS  whitespace-only text node 는 phone 매칭 skip — 원본 linkify.js:111/130 필터

import linkifyElement from 'linkify-element'
import * as linkify from 'linkifyjs'

// L3 / C4 — 모듈 로드 시점에 즉시 등록. optionalSlashSlash=true 가 아니면
// `sip://...` 만 인식되어 `sip:user@host` 형식이 email 로 잘못 분류됨.
linkify.registerCustomProtocol('sip', true)

const TEL_BRIDGE_BOUND = 'data-tel-bridge-bound'
const IGNORE_TAGS = ['a', 'script', 'style', 'textarea', 'button']

// noProtocolUrl / httpOrMailtoUrl + sip 매칭 범위와 동등.
const URL_ALLOW_RE = /^(https?:\/\/|ftp:\/\/|www\.|mailto:|sip:)/i

// §5.1 (B) — sip 은 user@host.tld 형식만 허용. linkifyjs 가 sip 을 type='url'
// 로 분류하므로 validate.url 안에서 추가 검증.
// /i flag — RFC 3261 case-insensitive 규정 (`SIP:` 대문자도 허용).
const SIP_VALIDATE_RE =
  /^sip:[a-zA-Z0-9+._%-]{1,256}@[a-zA-Z0-9][a-zA-Z0-9-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,25})+$/i

// 원본 linkify.js 의 phone 정규식 그대로. matchAll 사용 — global regex 의
// lastIndex 부작용 없음 + 코드 간결.
const PHONE_RE = /([\d+(]\d*[-) /.]*\d{2,}[-) /.]*\d{2,}[-) /.]*\d{2,}[ ,;|ext]*([#0-9]+))/g

export interface LinkifyMailOptions {
  /** tel: 링크 클릭 시 native bridge 호출 (모바일 WebView). 기본 true. */
  hijackTelClick?: boolean
}

export function linkifyMailNode(node: HTMLElement, options: LinkifyMailOptions = {}): void {
  const hijackTel = options.hijackTelClick !== false

  linkifyElement(node, {
    // H1 — `www.` URL 의 prefix 를 `https://` 로 유지 (이제 https 가 기본).
    defaultProtocol: 'https',
    ignoreTags: IGNORE_TAGS,

    // TR (§5.7 (B)) — http(s)/ftp/www 외부 URL 에만 target/rel 부착.
    // mailto:, sip:, bare email 은 제외 (원본 linkify.js 동작 보존).
    // body-level click handler ([EmailView/index.tsx:1044-1058]) 가 99% 가로채지만,
    // middle-click / context menu 시 동작 차이를 막기 위해 함수 분기.
    target: (href, type) => {
      if (type !== 'url') return null
      if (href.toLowerCase().startsWith('mailto:')) return null
      if (href.toLowerCase().startsWith('sip:')) return null
      return '_blank'
    },
    rel: (href, type) => {
      if (type !== 'url') return null
      if (href.toLowerCase().startsWith('mailto:')) return null
      if (href.toLowerCase().startsWith('sip:')) return null
      return 'noopener noreferrer'
    },

    // C1 — bare 도메인 자동 링크 차단 + §5.1 (B) sip 강제.
    validate: {
      url: (value) => {
        if (!URL_ALLOW_RE.test(value)) return false
        if (value.toLowerCase().startsWith('sip:')) return SIP_VALIDATE_RE.test(value)
        return true
      },
    },

    // events 옵션 미사용 — linkifyjs 는 tel: anchor 를 생성하지 않음.
    // (sip 은 type='url', phone 은 §5.2 의 hybrid 후처리). tel click handling 은
    // applyPhoneFallback + bindTelClickToExistingAnchors 가 담당.
  })

  // C2 — 본문에 이미 들어있던 tel anchor 에도 listener 부착 + 중복 가드.
  if (hijackTel) {
    bindTelClickToExistingAnchors(node)
  }

  // §5.2 (B) — phone hybrid 후처리. hijackTel=false 면 phone 변환은 그대로 하되
  // 새로 만든 tel anchor 에 listener 부착만 skip (테스트 / 비-WebView 컨텍스트용).
  applyPhoneFallback(node, hijackTel)
}

function bindTelClickToExistingAnchors(root: HTMLElement): void {
  const anchors = root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:" i]')
  anchors.forEach((a) => {
    if (a.getAttribute(TEL_BRIDGE_BOUND) === '1') return
    a.setAttribute(TEL_BRIDGE_BOUND, '1')
    a.addEventListener('click', handleTelClick)
  })
}

interface NativeBridge {
  nativeCall: (name: string, cb: null, params: unknown[]) => unknown
}

function handleTelClick(e: Event): void {
  const a = e.currentTarget as HTMLAnchorElement
  const href = a.href
  if (!href.toLowerCase().startsWith('tel:')) return
  e.stopPropagation()
  e.preventDefault()

  const w = window as unknown as {
    mvObj?: { colorMgr?: NativeBridge }
    docUtil?: { colorMgr?: NativeBridge }
  }
  const bridge = w.mvObj?.colorMgr ?? w.docUtil?.colorMgr

  // C3 — `result === false` 비교 의미 보존.
  // §5.6 운영자 답변: viewerExt.tsx:564 onClickPhoneNumber 가 void 반환 (parent 가 모든 처리 담당).
  // 따라서 bridge 가 존재하는 통상 경로에서는 result 가 undefined → fallback 미실행 (의도된 동작).
  // fallback 이 실행되는 경로: bridge 미존재 (mvObj / docUtil 둘 다 undefined — 비-WebView).
  const result = bridge ? bridge.nativeCall('onClickPhoneNumber', null, [href]) : false
  if (result === false) window.location.href = href
}

// H3 / WS — phone fallback 도 IGNORE_TAGS skip + whitespace-only 필터.
function applyPhoneFallback(root: Node, attachClickHandler: boolean): void {
  const stack: Node[] = [root]
  while (stack.length > 0) {
    const n = stack.pop() as Node
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as HTMLElement
      if (IGNORE_TAGS.indexOf(el.tagName.toLowerCase()) !== -1) continue
      Array.from(el.childNodes).forEach((c) => stack.push(c))
      continue
    }
    if (n.nodeType !== Node.TEXT_NODE) continue
    const text = n.nodeValue ?? ''
    if (text.length < 2 || !/\S/.test(text)) continue
    if (!hasPhoneMatch(text)) continue
    replaceTextWithPhoneLinks(n as Text, attachClickHandler)
  }
}

function hasPhoneMatch(text: string): boolean {
  for (const _ of text.matchAll(PHONE_RE)) {
    return true
  }
  return false
}

function replaceTextWithPhoneLinks(textNode: Text, attachClickHandler: boolean): void {
  const text = textNode.nodeValue ?? ''
  const parent = textNode.parentNode
  const ownerDoc = textNode.ownerDocument
  if (parent == null || ownerDoc == null) return

  const frag = ownerDoc.createDocumentFragment()
  let lastIndex = 0
  let matchedAny = false
  for (const m of text.matchAll(PHONE_RE)) {
    matchedAny = true
    const matchText = m[1]
    const matchStart = m.index ?? 0
    if (matchStart > lastIndex) {
      frag.appendChild(ownerDoc.createTextNode(text.slice(lastIndex, matchStart)))
    }
    const a = ownerDoc.createElement('a')
    a.href = `tel:${matchText}`
    a.textContent = matchText
    if (attachClickHandler) {
      a.addEventListener('click', handleTelClick)
      a.setAttribute(TEL_BRIDGE_BOUND, '1')
    }
    frag.appendChild(a)
    lastIndex = matchStart + matchText.length
  }
  if (!matchedAny) return
  if (lastIndex < text.length) {
    frag.appendChild(ownerDoc.createTextNode(text.slice(lastIndex)))
  }
  parent.replaceChild(frag, textNode)
}
